import { GeometryTypes } from "@/helpers/enums"
import ApiDataModel, { GeometryData } from "@/models/apidatamodel"
import DynamicRightSlotData from "@/models/dynamicrighslotdata"
import TableData from "@/models/tabledata"
import moment from "moment"
import AreaUtils from "@/helpers/areaUtils"
import { store } from '@/store/store'
import i18n from '@/i18n'

const t = i18n.global.t

export interface ViatJaHairiot {
    id: number;
    julkaisuaika: string;
    havaintoaika: string;
    alkuaika: string;
    loppuaika: string;
    vakavuus: string;
    kuvaus: string;
    vaikutusalue: string;
    vaikutusalueenTaloudet: string;
    koordinaatit: GeometryData;
    operaattori: string | null;
    operaattoriUrl: string | null;
    operaattoriMapUrl: string | null;
    operaattoriMapUrl2: string | null;
    rightSlotData?: DynamicRightSlotData | null
    popup: string;
}

export default class ViatJaHairiotService {
    AreaUtils = new AreaUtils()

    get currentLanguage() {
        return store.getters.activeLang
    }

    // #region ViatJaHairiot
    // TODO: Kartalle ei piirretä suoraan kaikkia alueita vaan ne pitäisi suodattaa rightslotin sliderin mukaan jotenkin...
    async handleDataViatJaHairiot(data): Promise<ApiDataModel[]> {
        try {
            const areaType = this.AreaUtils.getAreaTypeFromText(store.getters.selectedAreaRadioButton)
            // Haetaan ensin maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType)

            const result = data
            const tableValues: Map<string[], boolean> = new Map()
            result.forEach((value) => {
                if (value.koordinaatit) {
                    const options = {
                        fillColor: "green",
                        fillOpacity: 0.8,
                        color: "#6D6D6D",
                        opacity: 0.8,
                        weight: 1,
                        fill: true,
                        stroke: true,
                        id: value.id
                    }
                    /** täyttöväri vakavuuden mukaan */
                    switch (value.vakavuus.toLowerCase()) {
                    case "0":
                        options.fillColor = "#009FE366"
                        options.fillOpacity = 0.7
                        break
                    case "1":
                        options.fillColor = "#D6000066"
                        options.fillOpacity = 0.7
                        break
                    case "2":
                        options.fillColor = "#FFAA0066"
                        options.fillOpacity = 0.7
                        break
                    }
                    value.koordinaatit.options = options
                    value.koordinaatit.handleOnClick = true
                    value.koordinaatit.allowedGeometryTypes = [GeometryTypes.GeometryCollection, GeometryTypes.MultiPolygon, GeometryTypes.Polygon]

                    let popup = ""
                    if (value.operaattori) {
                        const startText = t("App.ViatJaHairiot.Alkoi") ?? "App.ViatJaHairiot.Alkoi"
                        const startDate = moment(value.alkuaika).format("DD.MM.YYYY HH:mm") ?? "?"
                        const endText = t("App.ViatJaHairiot.Paattyi") ?? "App.ViatJaHairiot.Paattyi"
                        const endDate = moment(value.loppuaika).format("DD.MM.YYYY HH:mm") ?? "?"
                        popup += "<div><b>" + value.operaattori + "</b></div><br/>"
                        popup += "<div>" + startText + "&nbsp;</div>"
                        popup += "<div>" + startDate + "&nbsp;</div><br/>"
                        popup += "<div>" + endText + "&nbsp;</div>"
                        popup += "<div>" + endDate + "&nbsp;</div>"
                        popup += "<br/>"

                        // kerätään righslotin tableen dataa
                        tableValues.set([startDate, endDate, value.operaattori], false)
                    }
                    value.popup = popup

                    models.push(new ApiDataModel("Fault_" + value.id.toString(), true, value.koordinaatit, value, null, null))
                }
            })
            store.dispatch("setTableData", new TableData(this.tableColumHeaders, null, tableValues))

            return models
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    get tableColumHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.ViatJaHairiot.Alkoi") ?? "App.ViatJaHairiot.Alkoi")
        columns.push(t("App.ViatJaHairiot.Paattyi") ?? "App.ViatJaHairiot.Paattyi")
        columns.push(t("App.ViatJaHairiot.Palveluntarjoaja") ?? "App.ViatJaHairiot.Palveluntarjoaja")
        return columns
    }

    getUpdatedtext(time: string, includeText: boolean = true):string {
        const updatedtext = t("App.LaajakaistaJaPuhelin.DataUpdated")
        let updatedTime = "?"
        if (moment(time).format("MM/YYYY")) {
            updatedTime = t("App.Posti.DataUpdated") + " " + moment(time).format("MM/YYYY")
        }
        return includeText === true ? updatedtext + updatedTime : updatedTime
    }

    getViatJaHairiotLegend(): string {
        let legend = "<div class=\"legend-item\"><i style=\"background: #ef9999\"></i><div>" + t("App.ViatJaHairiot.PalveluEstynyt") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: #ffdd99\"></i><div>" + t("App.ViatJaHairiot.PalveluHeikentynyt") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: #99d9f4\"></i><div>" + t("App.ViatJaHairiot.Suunniteltukäyttökatko") + "</div></div>"

        return legend
    }

    async getViatJaHairiot(slotData: DynamicRightSlotData | null): Promise<void> {
        const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
        if (selectedAreaRadioButton === "maakunta") {
            store.dispatch("setLoading", { name: "viatjahairiot", value: true })
            // Asetetaan oikean slotin sisältö
            store.dispatch("setRightSlot", "ViatJaHairiotTiedot")

            // haetaan lomakkeen tallentamat tiedot
            if (!slotData) slotData = new DynamicRightSlotData("viat")

            // haetaan maakunnan nimi
            const regions = this.AreaUtils.getRegions()
            const maakunta = regions.find(a => a.maakuntanumero === store.getters.selectedProvince)
            if (this.currentLanguage !== "sv") {
                slotData.municipality = maakunta?.nimi ?? ""
            }
            else {
                slotData.municipality = maakunta?.namn ?? ""
            }

            // Viedään rightslottiin maakunnan nimi
            store.dispatch("setRightSlotData", slotData)

            const filters = new URLSearchParams()
            const dates = this.getTimelineToDates(slotData?.filters?.get("Timeline") ?? "Viimeisin12kk")
            filters.append("alkuaika", dates[0])
            filters.append("loppuaika", dates[1])

            if (selectedAreaRadioButton === "maakunta") filters.append("maakuntanumero", store.getters.selectedProvince)

            store.dispatch("setDataUrlToLoad", { url: "v1/Viestinta/viatjahairiot/ViatJaHairiot", type: "ViatJaHairiot", params: filters })

            store.dispatch("setLoading", { name: "viatjahairiot", value: false })
        }
    }

    // // rightslotdataan on tallentunut lomakkeen puolelta kaikki tarvittavat hakuparametrit
    getSlotData(): DynamicRightSlotData | null {
        return (store.getters.rightSlotData as DynamicRightSlotData)
    }

    getTimelineToDates(timeline: string): Array<string> {
        const dates: Array<string> = []
        if (timeline === "Viimeisin12kk") {
            let date = new Date()
            date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
            date.setMonth(date.getMonth() - 12)
            dates.push(date.toISOString()) // alkuaika = nykyhetki - 12 kk
            dates.push(new Date().toISOString()) // loppuaika = nykyhetki
        }
        else {
            const year: number = +timeline
            dates.push(new Date(Date.UTC(year, 0, 1)).toISOString()) // alkuaika = valitun vuoden eka päivä, huom kuukaudet alkavat nollasta
            dates.push(new Date(Date.UTC(year, 11, 31, 23, 59)).toISOString()) // loppuaika = valitun vuoden viimeine päivä, huom kuukaudet alkavat nollasta
        }
        return dates
    }
    // #endregion
}
